import { useState } from 'react';
import { useEffect } from 'react';
import { DesktopCalendar } from './components/DesktopCalendar';
import { MobileCalendar } from './components/MobileCalendar';
import { DateTime } from 'luxon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { DateCalendarSlots, PickersDay, PickersDayProps, DateCalendarSlotProps } from '@mui/x-date-pickers';
import { useDebouncedCallback } from 'use-debounce';

export interface CalendarProps  {
    onMonthDecrease?: (date: any) => void,
    onMonthIncrease?: (date: any) => void,
    onPeriodChange?: (date: any) => void,
    onStartDateChange?: (date: any) => void,
    onEndDateChange?: (date: any) => void,
    onIntervalChange?: (event: any) => void,
    value?: DateTime,
    period?: DateTime,
    loading?: boolean,
    slots: any
};


export interface SubCalendarProps extends CalendarProps {
    startDate: DateTime,
    endDate: DateTime,
    sxCalendar?: SxProps<Theme>
};

export const Calendar = (props: CalendarProps): JSX.Element => {
    const {
        onMonthDecrease,
        onMonthIncrease,
        onPeriodChange,
        onStartDateChange,
        onEndDateChange,
        onIntervalChange,
        value = DateTime.fromJSDate(new Date()),
        loading = false,
        slots,
    } = props;


    const [sxCalendar, setSxCalendar] = useState<SxProps<Theme>>({
        width: "100%",
        '&': {
            height: `auto`,
            maxHeight: `100%`,
        },
        "& .MuiDayCalendar-monthContainer": {
            position: 'static'
        },
        "& .MuiDayCalendar-weekDayLabel": {
            width: {
                xs: '40px',
                md: '46px',
            },
            fontSize: '0.8rem',
            color: '#000000de'
        },
        "& .MuiPickersDay-root": {
            width: {
                xs: '40px',
                md: '46px',
            },
            height: {
                xs: '40px',
                md: '46px',
            },
            fontSize: '0.8rem'
        },
        "& .MuiPickersDay-today": {
            border: '4px solid #42a5f5'
        },
        "& .MuiPickersDay-today:not(.Mui-selected)": {
            border: '4px solid #42a5f5'
        },
        "& .MuiPickersDay--ok": {
            backgroundColor: '#4caf50',
            color:'#ffffff',
        }, 
        "& .MuiPickersDay--ok:hover": {
            backgroundColor: '#648AAF',
            color: '#FFF'
        },
        "& .MuiPickersDay--past-day": {
            backgroundColor: '#d4d4d4',
            color: '#FFF'
        },

        "& .MuiPickersDay--error": {
            backgroundColor: '#ef5350',
            color: '#ffffff',
        },
        "& .MuiPickersDay--error:hover": {
            backgroundColor: '#648AAF',
            color: '#FFF'
        },
        "& .MuiPickersDay--selected": {
            backgroundColor: '#648aaf'
        },
    });

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [period, setPeriod] = useState<any>(props.period || DateTime.fromJSDate(new Date()));
    const [startDate, setStartDate] = useState<DateTime>(period);
    const [endDate, setEndDate] = useState<DateTime>(period?.plus({ months: 1 }));

    useEffect(() => {    
        if (props.period !== undefined) {
            setPeriod(props.period)
        }
     
    }, [
        props.period
    ])

    useEffect(() => {
        if (period === undefined) {
            return
        }

        setStartDate(period.startOf('month'))
        setEndDate(period.plus({ months: 1 }).endOf('month'))
    }, [
        period
    ])


    useEffect(() => {
        if (startDate === undefined) {
            return
        }

        if (endDate === undefined) {
            return
        }
        onIntervalChange?.(
            {
                startDate: startDate,
                endDate: endDate
            }
        )

    }, [
        startDate, 
        endDate, 
        onIntervalChange
    ])

    const onMonthDecreaseHandler = (date: any) => {
        setPeriod(period.minus({ months: 1 }));
        onMonthDecrease?.(period)
    }

    const onMonthIncreaseHandler = (date: any) => {
        setPeriod(period.plus({ months: 1 }));
        onMonthDecrease?.(period)
    }

    const additionalProps = {
        onMonthIncrease: onMonthIncreaseHandler,
        onMonthDecrease: onMonthDecreaseHandler,
        startDate: startDate,
        endDate: endDate,
        loading: loading,
        sxCalendar: sxCalendar
    }

    const render = () => {
        if (isSmallScreen) {
            return (
                <MobileCalendar {...props} {...additionalProps}></MobileCalendar>
            )
        }
        return (
            <DesktopCalendar {...props}  {...additionalProps}></DesktopCalendar>
        )
    }

    return (
        <>
            {
                
                render()
            }
        </>
    )
}